
import { defineComponent, reactive, nextTick } from "vue";
import router from "@/router";

import CourseGroupsService from "@/api/Aula/CourseGroupsService";
import SystemSubjectsService from "@/api/Academic/SystemSubjectsService";
import HeadquartersService from "@/api/Configuration/HeadquartersService";
import AcademicYearService from "@/api/Configuration/AcademicYearService";
import CoursesService from "@/api/Aula/CoursesSevice";
import TeachersService from "@/api/Aula/TeachersService";
import AcademicTeacherAssigmentService from "@/api/Academic/AcademicTeacherAssigmentService";
// import AcademicLoadService from "@/api/Aula/AcademicLoadService";
import store from "@/store";

import type {
  ICourseGroupsModel,
  IHeadquartersModel,
  IAcademicYearModel,
  ISystemSubjectsModel,
  ICoursesModel,
  ITeachersModel,
} from "@/interfaces/modules";
import { SHOW_PRESCHOOL } from "@/enums";

type SubjectItem = { SubjectId: number; HoursPerWeek: number; TeacherHoursCapacity: number; };
type TeacherNode = { TeacherId: number; Subjects: SubjectItem[]; };
type GroupNode = { GroupId: number; Teachers: TeacherNode[]; };
type RegisterPayload = { AssigmentId?: number; Groups: GroupNode[] };  // <— NEW

export default defineComponent({
  name: "CourseGroupsSubjectsAssign",
  data() {
    return {
      assignmentId: this.$route.params.id || 0,
      table: { loading: false } as any,

      groupsRaw: [] as ICourseGroupsModel[],
      courses: [] as ICoursesModel[],
      groups: [] as Array<ICourseGroupsModel & {
        CourseName?: string;
        HeadquarterId?: number;
        AcademicYearId?: number;
        HeadquarterName?: string;
        AcademicYearName?: string;
      }>,
      subjects: [] as (ISystemSubjectsModel & { HoursPerWeek?: number; TeacherHoursCapacity?: number })[],
      headquarters: [] as IHeadquartersModel[],
      academicYears: [] as IAcademicYearModel[],
      teachers: [] as ITeachersModel[],

      filters: reactive({
        headquarterId: null as number | null,
        academicYearId: null as number | null,
        courseGroupId: null as number | null,
        teacherId: null as number | null,
      }),

      showOnlySelectedMap: {} as Record<string, boolean>,

      // ÚNICA FUENTE DE VERDAD (por selección)
      picked: [] as Array<{
        GroupId: number;
        TeacherId: number;
        SubjectId: number;
        AssignedHours: number;          // antes: HoursPerWeek (asignadas)
        CapacityHours: number;          // antes: TeacherHoursCapacity (independiente)
      }>,
    };
  },

  computed: {
    headquarterOptions(): Array<{ value: number; text: string }> {
      return (this.headquarters || [])
        .map(h => ({ value: h.Id, text: h.Description }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    academicYearOptions(): Array<{ value: number; text: string }> {
      return (this.academicYears || [])
        .map(y => ({ value: y.Id, text: `Año ${y.Year}` }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    groupOptions(): Array<{ value: number; text: string }> {
      return (this.groups || [])
        .map(g => ({ value: g.Id, text: g.Name || `Grupo ${g.Id}` }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    teacherOptions(): Array<{ value: number; text: string }> {
      return (this.teachers || [])
        .map(t => ({ value: t.Id, text: this.teacherLabel(t) }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },

    groupsForTree(): Array<ICourseGroupsModel & { CourseName?: string; HeadquarterName?: string; AcademicYearName?: string }> {
      const byHQ = (g: any) => this.filters.headquarterId == null || Number(g.HeadquarterId) === Number(this.filters.headquarterId);
      const byYear = (g: any) => this.filters.academicYearId == null || Number(g.AcademicYearId) === Number(this.filters.academicYearId);
      const byGroup = (g: any) => this.filters.courseGroupId == null || Number(g.Id) === Number(this.filters.courseGroupId);
      return (this.groups || []).filter(g => byHQ(g) && byYear(g) && byGroup(g))
        .sort((a, b) => (a.Name || "").localeCompare(b.Name || ""));
    },

    teachersForUI(): ITeachersModel[] {
      return (this.teachers || []).filter(t =>
        this.filters.teacherId == null || Number(t.Id) === Number(this.filters.teacherId)
      );
    },

    canRegister(): boolean {
      return this.picked.length > 0;
    },

    isEditing(): boolean {
      return Number(this.assignmentId) > 0;
    },

    subjectTableHeaders(): any[] {
      return [
        { key: "Selected", label: "", sortable: false, thStyle: { width: "26px" }, thClass: "py-0 text-center", tdClass: "py-0 text-center" },
        { key: "SubjectName", label: "Materia", sortable: true, thClass: "py-0", tdClass: "py-0" },
        { key: "HoursToAssign", label: "Horas a asignar (h/sem)", sortable: false, thClass: "py-0", tdClass: "py-0 text-center" },
        { key: "TeacherHoursCapacity", label: "Capacidad docente (h/sem)", sortable: false, thClass: "py-0", tdClass: "py-0" },
      ];
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    teacherLabel(t: any) {
      return `${t.Name} ${t.LastName || ""}`.trim();
    },
    nodeKey(groupId: number, teacherId: number) {
      return `${groupId}_${teacherId}`;
    },

    async load() {
      try {
        this.table.loading = true;

        const [groups, courses, subjects, headquarters, years, teachers] = await Promise.all([
          CourseGroupsService.GetCourseGroups(),
          CoursesService.GetAllExcludingPreschool(),
          SystemSubjectsService.GetSubjects(),
          HeadquartersService.GetHeadquarters(),
          AcademicYearService.GetAcademicYears(),
          TeachersService.GetTeachers?.() ?? Promise.resolve([]),
        ]);

        this.groupsRaw = groups || [];
        this.courses = courses || [];
        this.headquarters = headquarters || [];
        this.academicYears = years || [];
        this.teachers = teachers || [];

        console.log("Loaded courses:", this.courses);

        const crsById = new Map(this.courses.map(c => [Number(c.Id), c]));
        const hqById = new Map(this.headquarters.map(h => [Number(h.Id), h]));
        const yrById = new Map(this.academicYears.map(y => [Number(y.Id), y]));

        this.groups = this.groupsRaw
          .filter((g: ICourseGroupsModel) => crsById.has(Number(g.CourseId)))
          .map((g: ICourseGroupsModel) => {
            const course = crsById.get(Number(g.CourseId));
            const headquarterId = course?.HeadquarterId ?? undefined;
            const yearId = course?.AcademicYearId ?? g.AcademicYearId;
            return {
              ...g,
              CourseName: course?.Name ?? "",
              HeadquarterId: headquarterId,
              AcademicYearId: yearId,
              HeadquarterName: headquarterId ? (hqById.get(headquarterId)?.Description ?? "") : "",
              AcademicYearName: yearId ? (`Año ${yrById.get(yearId)?.Year ?? ""}`) : "",
            };
          });

        this.subjects = (subjects || []).map((s: any) => ({
          ...s,
          HoursPerWeek: s.HoursPerWeek ?? 0,
          TeacherHoursCapacity: s.TeacherHoursCapacity ?? 0,
        }));

        // ===== NEW: when editing an existing assignment, load payload and lock to that teacher =====
        if (this.isEditing) {
          const dto = await AcademicTeacherAssigmentService.GetTeacherAssignmentById(Number(this.assignmentId));
          // hydrate picked
          this.picked = [];
          for (const g of dto.Groups || []) {
            for (const t of g.Teachers || []) {
              for (const s of t.Subjects || []) {
                this.picked.push({
                  GroupId: g.GroupId,
                  TeacherId: t.TeacherId,
                  SubjectId: s.SubjectId,
                  AssignedHours: Number(s.HoursPerWeek || 0),
                  CapacityHours: Number(s.TeacherHoursCapacity || s.HoursPerWeek || 0),
                });
              }
            }
          }

          // enforce filters for this assignment
          this.filters.academicYearId = dto.AcademicYearId;

          // derive the single teacherId from payload
          const teacherIds = new Set<number>();
          for (const g of dto.Groups || []) for (const t of g.Teachers || []) teacherIds.add(Number(t.TeacherId));
          const onlyTeacherId = Array.from(teacherIds)[0] || null;

          // filter UI to show only that teacher
          if (onlyTeacherId != null) {
            this.filters.teacherId = onlyTeacherId;
            // also trim the list so teacher dropdown shows only that teacher
            this.teachers = (this.teachers || []).filter(tt => Number(tt.Id) === Number(onlyTeacherId));
          }
        }

        await nextTick();
      } finally {
        this.table.loading = false;
      }
    },

    // ======== P I C K E D  (única fuente de verdad) ========
    _pickedIndex(g: number, t: number, s: number) {
      return this.picked.findIndex(p => p.GroupId === g && p.TeacherId === t && p.SubjectId === s);
    },
    isSelected(g: number, t: number, s: number) {
      return this._pickedIndex(g, t, s) !== -1;
    },

    currentAssigned(g: number, t: number, s: number) {
      const i = this._pickedIndex(g, t, s);
      return i === -1 ? 0 : Number(this.picked[i].AssignedHours || 0) || 0;
    },
    currentCapacity(g: number, t: number, s: number, fallback: number) {
      const i = this._pickedIndex(g, t, s);
      return i === -1 ? Number(fallback || 0) : Number(this.picked[i].CapacityHours || 0) || 0;
    },

    _upsertPicked(g: number, t: number, s: number, assigned: number, capacity: number | null = null) {
      const i = this._pickedIndex(g, t, s);
      if (assigned > 0 || (capacity !== null && capacity > 0)) {
        // ensure record exists
        if (i === -1) {
          this.picked.push({
            GroupId: g,
            TeacherId: t,
            SubjectId: s,
            AssignedHours: Math.max(0, assigned),
            CapacityHours: Math.max(0, capacity ?? assigned),
          });
        } else {
          if (!Number.isNaN(assigned)) this.picked[i].AssignedHours = Math.max(0, assigned);
          if (capacity !== null && !Number.isNaN(capacity)) this.picked[i].CapacityHours = Math.max(0, capacity);
        }
        // auto-clean if everything is 0
        const idx = this._pickedIndex(g, t, s);
        if (idx !== -1 && this.picked[idx].AssignedHours === 0 && this.picked[idx].CapacityHours === 0) {
          this.picked.splice(idx, 1);
        }
      } else if (i !== -1) {
        // both at 0 -> remove
        this.picked.splice(i, 1);
      }
    },

    onToggleSelection(g: number, t: number, s: number, newVal: boolean, defaultAssigned: number, defaultCapacity: number) {
      // Selecciona => asegura valores por defecto (>0). Deselecciona => pone ambos en 0 (se remueve).
      if (newVal) {
        const assigned = Math.max(1, Number(this.currentAssigned(g, t, s)) || Number(defaultAssigned) || 1);
        const capacity = Math.max(assigned, Number(this.currentCapacity(g, t, s, defaultCapacity)) || Number(defaultCapacity) || assigned);
        this._upsertPicked(g, t, s, assigned, capacity);
      } else {
        this._upsertPicked(g, t, s, 0, 0);
      }
    },

    onHoursChange(g: number, t: number, s: number, newAssigned: number) {
      const assigned = Math.max(0, Number(newAssigned) || 0);
      // keep capacity if exists; if record absent, default capacity = assigned
      const curCap = this.currentCapacity(g, t, s, assigned);
      this._upsertPicked(g, t, s, assigned, curCap);
    },

    onCapacityChange(g: number, t: number, s: number, newCapacity: number) {
      const capacity = Math.max(0, Number(newCapacity) || 0);
      const curAssigned = this.currentAssigned(g, t, s);
      // If capacity drops below assigned, keep both as-is (business rule up to you). You could clamp if needed.
      this._upsertPicked(g, t, s, curAssigned, capacity);
    },

    // ======== R E N D E R  H E L P E R S ========
    subjectRowsForNode(groupId: number, teacherId: number) {
      const node = this.nodeKey(groupId, teacherId);
      const onlySel = !!this.showOnlySelectedMap[node];

      const rows = (this.subjects || []).map(s => ({
        ...s,
        _ctx: { g: groupId, t: teacherId }
      }));

      return onlySel
        ? rows.filter(r => this.isSelected(groupId, teacherId, Number(r.Id)))
        : rows;
    },

    getNodeSelectedCount(groupId: number, teacherId: number): number {
      return this.picked.filter(p => p.GroupId === groupId && p.TeacherId === teacherId).length;
    },

    totalHoursByGroupTeacher(groupId: number, teacherId: number): number {
      return this.picked
        .filter(p => p.GroupId === groupId && p.TeacherId === teacherId)
        .reduce((acc, p) => acc + Number(p.AssignedHours || 0), 0);
    },

    // ======== P A Y L O A D  (Groups -> Teachers -> Subjects) ========
    buildPayloadFromPicked(): RegisterPayload {
      const groupMap = new Map<number, Map<number, SubjectItem[]>>();

      for (const p of this.picked) {
        const g = Number(p.GroupId);
        const t = Number(p.TeacherId);
        const s: SubjectItem = {
          SubjectId: Number(p.SubjectId),
          HoursPerWeek: Number(p.AssignedHours || 0),
          TeacherHoursCapacity: Number(p.CapacityHours || 0), // ahora independiente
        };

        if (!groupMap.has(g)) groupMap.set(g, new Map());
        const teacherMap = groupMap.get(g)!;
        if (!teacherMap.has(t)) teacherMap.set(t, []);
        teacherMap.get(t)!.push(s);
      }

      const Groups: GroupNode[] = Array.from(groupMap.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([GroupId, teacherMap]) => {
          const Teachers: TeacherNode[] = Array.from(teacherMap.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([TeacherId, Subjects]) => ({
              TeacherId,
              Subjects: Subjects.sort((a, b) => a.SubjectId - b.SubjectId),
            }));
          return { GroupId, Teachers };
        });

      return {
        AssigmentId: Number(this.assignmentId) || 0,   // <— include it
        Groups
      };
    },

    async onRegisterAll() {
      if (!this.canRegister) return;

      const payload = this.buildPayloadFromPicked();

      try {
        this.table.loading = true;
        await AcademicTeacherAssigmentService.PostTeacherAssignment(payload);
        store.dispatch("core/popupDialog", {
          text: "Registro guardado con éxito.",
          type: "success",
          showCancelButton: false,
        });
        router.back();
      } catch (e) {
        console.error("Error registrando carga académica:", e);
      } finally {
        this.table.loading = false;
      }
    },

    goToSubject(id: number) {
      router.push(`/aula/subject-detail/${id}`);
    },
  },
});
